import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, User, Building2, Phone, CheckCircle, AlertCircle, Loader2, Bot, Bug, Network, FileSearch, Shield, Rocket, Eye, EyeOff, Lock } from 'lucide-react';
import { registration } from '../../services/api/client';
import fr from '../../i18n/locales/fr';
import './Register.css';

// Helper to get French translations for branding (always French on register page)
const getFr = (key) => {
  const keys = key.split('.');
  let value = fr;
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      return key;
    }
  }
  return typeof value === 'string' ? value : key;
};

// Email validation - all domains are now allowed
const isPersonalEmail = (email) => {
  // All email domains are now allowed
  return { valid: true, message: '' };
};

const Register = () => {
  // Use French translations for the registration form (French platform consistency)
  // This is a French platform - consistent with Login page (no language selector)
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = fr;
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return key;
      }
    }
    if (typeof value !== 'string') return key;
    return value.replace(/\{(\w+)\}/g, (match, param) => {
      return params[param] !== undefined ? params[param] : match;
    });
  };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Demo invitation params from URL
  const demoInvitationToken = searchParams.get('demo_invitation');
  const invitedEmail = searchParams.get('email');
  const isDemoInvited = !!demoInvitationToken;

  const [formData, setFormData] = useState({
    company_name: '',
    email: invitedEmail || '',
    first_name: '',
    last_name: '',
    phone: '',
    password: '',
    acceptTerms: false
  });
  const [showPassword, setShowPassword] = useState(false);

  // Pre-fill email if invitation params are present
  useEffect(() => {
    if (invitedEmail) {
      setFormData(prev => ({ ...prev, email: invitedEmail }));
    }
  }, [invitedEmail]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [success, setSuccess] = useState(false);

  // Check if email is professional
  const emailValidation = useMemo(() => isPersonalEmail(formData.email), [formData.email]);

  // For demo invited users, password is required immediately (no email confirmation flow)
  const isFormValid = formData.company_name && formData.email && formData.first_name && formData.last_name && formData.acceptTerms && emailValidation.valid
    && (!isDemoInvited || (formData.password && formData.password.length >= 8));

  // Matrix rain characters
  const matrixChars = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン01';

  // Generate matrix columns
  const matrixColumns = useMemo(() => {
    return Array.from({ length: 20 }, (_, i) => ({
      id: i,
      left: `${(i / 20) * 100}%`,
      delay: Math.random() * 10,
      duration: 15 + Math.random() * 15,
      chars: Array.from({ length: 30 }, () => matrixChars[Math.floor(Math.random() * matrixChars.length)]).join('\n')
    }));
  }, []);

  // Generate floating orbs
  const floatingOrbs = useMemo(() => {
    return Array.from({ length: 5 }, (_, i) => ({
      id: i,
      size: 200 + Math.random() * 300,
      left: Math.random() * 100,
      top: Math.random() * 100,
      delay: Math.random() * 5,
      duration: 20 + Math.random() * 10
    }));
  }, []);

  // Feature cards - always in French for branding consistency on this French platform
  const featureCards = [
    {
      icon: <Bot className="w-8 h-8" />,
      title: getFr('features.autonomousAI.title'),
      description: getFr('features.autonomousAI.description'),
      highlight: getFr('features.autonomousAI.highlight')
    },
    {
      icon: <Bug className="w-8 h-8" />,
      title: getFr('features.advancedDetection.title'),
      description: getFr('features.advancedDetection.description'),
      highlight: getFr('features.advancedDetection.highlight')
    },
    {
      icon: <Network className="w-8 h-8" />,
      title: getFr('features.completeTests.title'),
      description: getFr('features.completeTests.description'),
      highlight: getFr('features.completeTests.highlight')
    },
    {
      icon: <FileSearch className="w-8 h-8" />,
      title: getFr('features.instantReports.title'),
      description: getFr('features.instantReports.description'),
      highlight: getFr('features.instantReports.highlight')
    }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (error) setError('');
  };

  // Auto-fill +33 prefix when user focuses on empty phone field
  const handlePhoneFocus = (e) => {
    if (!formData.phone || formData.phone.trim() === '') {
      setFormData(prev => ({
        ...prev,
        phone: '+33 '
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) {
      setError(t('errors.fillAllFields'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await registration.registerCompany({
        company_name: formData.company_name,
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone || undefined,
        plan: 'free',
        ...(demoInvitationToken ? {
          demo_invitation_token: demoInvitationToken,
          password: formData.password
        } : {})
      });

      // Demo mode: the backend returns an access_token to auto-login the new lead
      // directly into the platform. No "check your email" intermediate screen.
      if (process.env.REACT_APP_DEMO_MODE === 'true' && response?.access_token) {
        localStorage.setItem('access_token', response.access_token);
        if (response.user_id || response.user) {
          localStorage.setItem('user', JSON.stringify(response.user || { id: response.user_id, email: response.email }));
        }
        navigate('/', { replace: true });
        return;
      }

      setSuccess(true);
    } catch (err) {
      console.error('Registration error:', err);
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          if (detail.includes('email')) {
            setError(t('errors.emailAlreadyUsed'));
          } else if (detail.includes('company')) {
            setError(t('errors.companyAlreadyRegistered'));
          } else {
            setError(detail);
          }
        } else {
          setError(t('errors.registrationError'));
        }
      } else if (err.code === 'ERR_NETWORK') {
        setError(t('errors.networkError'));
      } else {
        setError(t('errors.genericError'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (success) {
    return (
      <div className="register-page min-h-screen flex items-center justify-center p-4">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingOrbs.map(orb => (
            <div
              key={orb.id}
              className="floating-orb"
              style={{
                width: orb.size,
                height: orb.size,
                left: `${orb.left}%`,
                top: `${orb.top}%`,
                animationDelay: `${orb.delay}s`,
                animationDuration: `${orb.duration}s`
              }}
            />
          ))}
        </div>

        <div className="relative z-10 w-full max-w-md">
          <div className="bg-[#0d0d0d]/90 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 text-center"
               style={{ boxShadow: '0 0 50px rgba(0, 255, 136, 0.1)' }}>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-green/10 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-brand-green" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">
              {isDemoInvited ? 'Votre compte démo est prêt !' : t('auth.registrationSuccess')}
            </h2>
            <p className="text-gray-400 mb-6">
              {isDemoInvited ? (
                <>Votre compte a été créé avec succès pour <span className="text-white font-medium">{formData.email}</span>. Vous pouvez vous connecter immédiatement !</>
              ) : (
                <>{t('auth.registrationEmailSent')} <span className="text-white font-medium">{formData.email}</span>. {t('auth.clickLinkToSetPassword')}</>
              )}
            </p>
            {!isDemoInvited && (
              <p className="text-gray-500 text-sm mb-8">
                {t('auth.checkSpam')}
              </p>
            )}
            <Link
              to="/login"
              className="inline-flex items-center justify-center w-full bg-brand-green hover:bg-brand-green/90 text-black font-semibold py-3 rounded-xl transition-colors"
            >
              {t('auth.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="register-page min-h-screen flex">
      {/* Left Panel - Branding & Animation (same as Login) */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden bg-gradient-to-br from-[#0a0a0a] via-[#0d1117] to-[#050505]">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(rgba(0, 255, 136, 0.5) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(0, 255, 136, 0.5) 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
              animation: 'grid-move 20s linear infinite'
            }}
          />

          {/* Matrix Rain */}
          <div className="matrix-container absolute inset-0 overflow-hidden">
            {matrixColumns.map((col) => (
              <div
                key={col.id}
                className="matrix-column absolute top-0 text-brand-green/20 font-mono text-xs whitespace-pre leading-4"
                style={{
                  left: col.left,
                  animation: `matrix-fall ${col.duration}s linear infinite`,
                  animationDelay: `${col.delay}s`
                }}
              >
                {col.chars.split('\n').map((char, i) => (
                  <div key={i} style={{ opacity: Math.random() * 0.5 + 0.1 }}>{char}</div>
                ))}
              </div>
            ))}
          </div>

          {/* Multiple Radial Glows */}
          <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-brand-green/5 rounded-full blur-[120px]" style={{ contain: 'paint', overflow: 'hidden' }} />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-brand-green/3 rounded-full blur-[100px]" style={{ contain: 'paint', overflow: 'hidden' }} />

          {/* Floating Orbs */}
          <div className="floating-orb absolute top-1/4 left-1/4 w-32 h-32 bg-brand-green/10 rounded-full blur-[60px]" style={{ animation: 'float-slow 8s ease-in-out infinite', contain: 'paint', overflow: 'hidden' }} />
          <div className="floating-orb absolute bottom-1/3 right-1/3 w-24 h-24 bg-brand-green/10 rounded-full blur-[50px]" style={{ animation: 'float-slow 10s ease-in-out infinite reverse', contain: 'paint', overflow: 'hidden' }} />
          <div className="floating-orb absolute top-1/2 right-1/4 w-20 h-20 bg-cyan-500/10 rounded-full blur-[40px]" style={{ animation: 'float-slow 12s ease-in-out infinite', contain: 'paint', overflow: 'hidden' }} />

          {/* Scan Line */}
          <div className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-brand-green/50 to-transparent" style={{ animation: 'scan-line 4s linear infinite' }} />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full w-full px-8 xl:px-12 py-6">
          {/* Logo - Top Left */}
          <div className="mb-4">
            <img
              src="/logo.png"
              alt="SYLINK"
              className="h-14 xl:h-16 w-auto"
            />
          </div>

          {/* Main Title - Centered Vertically, Left Aligned */}
          <div className="flex-1 flex flex-col justify-center">
            <h1 className="text-5xl xl:text-6xl 2xl:text-7xl font-bold text-white mb-6 leading-tight">
              {getFr('branding.platformTitle')}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-green via-emerald-400 to-cyan-400">
                {getFr('branding.platformSubtitle')}
              </span>
            </h1>
            <p className="text-gray-400 text-xl xl:text-2xl 2xl:text-3xl max-w-xl leading-relaxed">
              {getFr('branding.platformDescription')}
            </p>
          </div>

          {/* Feature Cards Grid */}
          <div className="grid grid-cols-2 gap-3">
            {featureCards.map((feature, index) => (
              <div
                key={index}
                className="feature-card group relative p-3 xl:p-4 rounded-xl border border-brand-green/10 bg-gradient-to-br from-[#0d1117]/80 to-[#0a0a0a]/80 backdrop-blur-sm hover:border-brand-green/30 transition-all duration-300"
                style={{ animation: `fade-in-up 0.6s ease-out forwards`, animationDelay: `${0.2 + index * 0.1}s`, opacity: 0 }}
              >
                {/* Glow effect on hover */}
                <div className="absolute inset-0 rounded-xl bg-brand-green/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Icon */}
                <div className="relative w-10 h-10 xl:w-12 xl:h-12 rounded-lg bg-gradient-to-br from-brand-green/20 to-brand-green/5 border border-brand-green/20 flex items-center justify-center text-brand-green mb-2 group-hover:scale-110 transition-transform duration-300">
                  {React.cloneElement(feature.icon, { className: "w-5 h-5 xl:w-6 xl:h-6" })}
                </div>

                {/* Content */}
                <div className="relative">
                  <h3 className="text-white font-semibold text-sm xl:text-base mb-1">{feature.title}</h3>
                  <p className="text-gray-400 text-xs xl:text-sm mb-2 leading-snug line-clamp-3 break-words">{feature.description}</p>

                  {/* Highlight Badge */}
                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] xl:text-xs font-medium bg-brand-green/10 text-brand-green border border-brand-green/20">
                    {feature.highlight}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Stats Bar */}
          <div className="mt-6 pt-4 border-t border-brand-green/10">
            <div className="flex items-center justify-between">
              <div className="text-center">
                <div className="text-xl xl:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-emerald-400">99,9{'\u00A0'}%</div>
                <div className="text-[10px] xl:text-xs text-gray-400 mt-1.5">{getFr('stats.availability')}</div>
              </div>
              <div className="w-px h-8 bg-brand-green/20" />
              <div className="text-center">
                <div className="text-xl xl:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-emerald-400">+50{'\u00A0'}000</div>
                <div className="text-[10px] xl:text-xs text-gray-400 mt-1.5">{getFr('stats.vulnerabilities')}</div>
              </div>
              <div className="w-px h-8 bg-brand-green/20" />
              <div className="text-center">
                <div className="text-xl xl:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-emerald-400">24/7</div>
                <div className="text-[10px] xl:text-xs text-gray-400 mt-1.5">{getFr('stats.monitoring')}</div>
              </div>
              <div className="w-px h-8 bg-brand-green/20" />
              <div className="text-center">
                <div className="text-xl xl:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-emerald-400">&lt;{'\u00A0'}5{'\u00A0'}min</div>
                <div className="text-[10px] xl:text-xs text-gray-400 mt-1.5">{getFr('stats.scanTime')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-green/30 to-transparent" />
        <div className="absolute top-0 bottom-0 right-0 w-px bg-gradient-to-b from-transparent via-brand-green/30 to-transparent" />
      </div>

      {/* Right Panel - Registration Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12 relative">
        {/* Background for mobile */}
        <div className="absolute inset-0 lg:hidden overflow-hidden pointer-events-none">
          {floatingOrbs.slice(0, 3).map(orb => (
            <div
              key={orb.id}
              className="floating-orb"
              style={{
                width: orb.size * 0.5,
                height: orb.size * 0.5,
                left: `${orb.left}%`,
                top: `${orb.top}%`,
                animationDelay: `${orb.delay}s`,
                animationDuration: `${orb.duration}s`
              }}
            />
          ))}
        </div>

        <div className="w-full max-w-md relative z-10">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <img
              src="/logo.png"
              alt="SYLink Pentest"
              className="h-12 w-auto mx-auto"
            />
          </div>

          {/* Form Container */}
          <div className="bg-[#0d0d0d]/80 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 lg:p-10"
               style={{ boxShadow: '0 0 50px rgba(0, 255, 136, 0.1)' }}>

            {/* Demo Invitation Banner */}
            {isDemoInvited && (
              <div className="mb-6 p-4 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-start gap-3">
                <Rocket className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-purple-300 font-medium text-sm">Invitation démo SYLink Pentest</p>
                  <p className="text-purple-400/80 text-xs mt-1">Votre compte sera créé immédiatement sans validation requise. Complétez le formulaire ci-dessous.</p>
                </div>
              </div>
            )}

            {/* Header */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">
                {isDemoInvited ? 'Créer votre compte démo' : t('auth.createAccount')}
              </h2>
              <p className="text-gray-400 leading-relaxed">
                {isDemoInvited ? 'Renseignez vos informations pour accéder à votre espace démo.' : t('auth.fillFormBelowEmail')}
              </p>
              <p className="text-gray-500 text-xs mt-2">
                <span className="text-brand-green">*</span> {t('common.requiredFields')}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth.companyName')} <span className="text-brand-green">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    className="w-full bg-[#111111] border border-gray-800 rounded-xl pl-12 pr-4 py-3 text-white placeholder-gray-400
                             focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20
                             hover:border-gray-700 transition-all"
                    placeholder={t('auth.companyPlaceholder')}
                    required
                  />
                </div>
              </div>

              {/* First Name & Last Name in grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('auth.firstName')} <span className="text-brand-green">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      className="w-full bg-[#111111] border border-gray-800 rounded-xl pl-12 pr-4 py-3 text-white placeholder-gray-400
                               focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20
                               hover:border-gray-700 transition-all"
                      placeholder={t('auth.firstNamePlaceholder')}
                      required
                    />
                  </div>
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('auth.lastName')} <span className="text-brand-green">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      className="w-full bg-[#111111] border border-gray-800 rounded-xl pl-12 pr-4 py-3 text-white placeholder-gray-400
                               focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20
                               hover:border-gray-700 transition-all"
                      placeholder={t('auth.lastNamePlaceholder')}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {isDemoInvited ? 'Email' : t('auth.professionalEmail')} <span className="text-brand-green">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={isDemoInvited ? undefined : handleChange}
                    readOnly={isDemoInvited}
                    className={`w-full bg-[#111111] border rounded-xl pl-12 pr-4 py-3 text-white placeholder-gray-400
                             focus:outline-none focus:ring-2 transition-all
                             ${isDemoInvited
                               ? 'border-purple-500/30 opacity-80 cursor-not-allowed'
                               : !emailValidation.valid
                                 ? 'border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20'
                                 : 'border-gray-800 focus:border-brand-green/50 focus:ring-brand-green/20 hover:border-gray-700'}`}
                    placeholder={t('auth.professionalEmailPlaceholder')}
                    required
                  />
                </div>
                {/* Email validation error */}
                {!emailValidation.valid && formData.email && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {emailValidation.message === 'disposable'
                      ? t('errors.disposableEmailNotAllowed')
                      : t('errors.personalEmailNotAllowed', { domain: emailValidation.message })}
                  </p>
                )}
              </div>

              {/* Professional Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth.professionalPhone')} <span className="text-gray-500 font-normal">({t('common.optional')})</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onFocus={handlePhoneFocus}
                    className="w-full bg-[#111111] border border-gray-800 rounded-xl pl-12 pr-4 py-3 text-white placeholder-gray-400
                             focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20
                             hover:border-gray-700 transition-all"
                    placeholder={t('auth.phonePlaceholder')}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">{t('auth.phoneFormatHint')}</p>
              </div>

              {/* Password field - only for demo invited users */}
              {isDemoInvited ? (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Mot de passe <span className="text-brand-green">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full bg-[#111111] border border-gray-800 rounded-xl pl-12 pr-12 py-3 text-white placeholder-gray-400
                               focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20
                               hover:border-gray-700 transition-all"
                      placeholder="Minimum 8 caractères"
                      minLength={8}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {formData.password && formData.password.length < 8 && (
                    <p className="text-xs text-red-400 mt-1">Le mot de passe doit contenir au moins 8 caractères</p>
                  )}
                </div>
              ) : (
                /* Info message about password (normal flow - set via email) */
                <div className="p-4 rounded-xl bg-brand-green/10 border border-brand-green/20">
                  <p className="text-sm text-gray-300">
                    <span className="text-brand-green font-medium">{t('common.note')}</span> {t('auth.passwordEmailNote')}
                  </p>
                </div>
              )}

              {/* Terms acceptance */}
              <div className="pt-2">
                <label className="inline-flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={handleChange}
                    aria-label={t('auth.acceptTermsPrefix') + ' ' + t('footer.termsShort') + ' ' + t('common.andThe') + ' ' + t('footer.salesShort') + ' *'}
                    className="sr-only peer"
                  />
                  <div className={`w-5 h-5 min-w-[20px] rounded border-2 transition-all duration-200 flex items-center justify-center shrink-0
                    ${formData.acceptTerms
                      ? 'bg-brand-green border-brand-green'
                      : 'border-gray-300 group-hover:border-white'}`}
                  >
                    {formData.acceptTerms && (
                      <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm text-gray-300 group-hover:text-gray-200 transition-colors leading-5 min-w-0 break-words">
                    {t('auth.acceptTermsPrefix')}{' '}
                    <a href="https://sylink-pentest.com/cgu" target="_blank" rel="noopener noreferrer" className="text-brand-green hover:text-brand-green/80 font-semibold underline decoration-brand-green decoration-2 hover:decoration-brand-green/80 underline-offset-2 transition-colors" title={t('footer.terms')} onClick={(e) => e.stopPropagation()}>{t('footer.termsShort')}</a>
                    {' '}{t('common.andThe')}{' '}
                    <a href="https://sylink-pentest.com/cgv" target="_blank" rel="noopener noreferrer" className="text-brand-green hover:text-brand-green/80 font-semibold underline decoration-brand-green decoration-2 hover:decoration-brand-green/80 underline-offset-2 transition-colors" title={t('footer.sales')} onClick={(e) => e.stopPropagation()}>{t('footer.salesShort')}</a>
                    {' '}<span className="text-brand-green">*</span>
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading || !isFormValid}
                  className="w-full bg-brand-green hover:bg-brand-green/90 text-black font-semibold py-3.5 rounded-xl
                           transition-all transform hover:scale-[1.02] active:scale-[0.98]
                           disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                           flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>{t('auth.registering')}</span>
                    </>
                  ) : (
                    <span>{t('auth.createMyAccount')}</span>
                  )}
                </button>
              </div>
            </form>

            {/* Login link */}
            <div className="mt-8 text-center">
              <p className="text-gray-400 text-sm">
                {t('auth.alreadyHaveAccount')}{' '}
                <Link to="/login" className="text-brand-green hover:text-brand-green/80 font-medium underline decoration-brand-green/70 hover:decoration-brand-green underline-offset-2 transition-colors">
                  {t('auth.loginButton')}
                </Link>
              </p>
            </div>
          </div>

          {/* Footer */}
          <p className="mt-8 text-center text-gray-600 text-xs">
            {t('auth.byRegistering')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
